// client/src/api/ApiAdminTools.js
import flex from './FlexFetch'

const admin_tools_endpoint = '/api/1.0/admin-tools/'

async function impersonateUser(targetUserId, reason) {
  const response = await flex.flexFetch(admin_tools_endpoint + 'impersonate', {
    method: 'POST',
    body: JSON.stringify({ targetUserId, reason }),
    credentials: 'include',
  })
  const json = await flex.handleResponse(response)
  // Normalize strict envelope: server returns { success, data: { message, token, adminUser, targetUser } }
  return json.data !== undefined ? json.data : json
}

async function stopImpersonate() {
  const response = await flex.flexFetch(
    admin_tools_endpoint + 'stopImpersonate',
    {
      method: 'POST',
      credentials: 'include',
    },
  )
  const json = await flex.handleResponse(response)
  // Normalize strict envelope: server returns { success, data: { message, token, adminUser } }
  return json.data !== undefined ? json.data : json
}

async function getImpersonateStatus() {
  const response = await flex.flexFetch(
    admin_tools_endpoint + 'impersonateStatus',
    {
      method: 'GET',
      credentials: 'include',
    },
  )
  const json = await flex.handleResponse(response)
  // Normalize strict envelope: server returns { success, data: { isImpersonating, ... } }
  return json.data !== undefined ? json.data : json
}

async function getPageFlows(startDate, endDate, limit = 100, options = {}) {
  const { search } = options
  const params = new URLSearchParams()
  if (startDate) params.append('start_date', startDate)
  if (endDate) params.append('end_date', endDate)
  if (limit) params.append('limit', limit)
  if (search) params.append('search', search)

  const response = await flex.flexFetch(
    `${admin_tools_endpoint}getPageFlows?${params.toString()}`,
    {
      method: 'GET',
      credentials: 'include',
    },
  )
  const json = await flex.handleResponse(response)
  // Normalize strict envelope: server returns { success, data: { nodes, links, ... } }
  // Callers expect flat shape: { success, nodes, links, date_range, total_flows }
  if (json.data !== undefined) {
    return { success: json.success, ...json.data }
  }
  return json
}

async function updateUser(userId, updates) {
  const response = await flex.flexFetch(admin_tools_endpoint + 'updateUser', {
    method: 'POST',
    body: JSON.stringify({ userId, updates }),
    credentials: 'include',
  })
  const json = await flex.handleResponse(response)
  // Normalize strict envelope: server returns { success, data: { message, user } }
  // Callers expect: { success, user, message }
  if (json.data !== undefined) {
    return { success: json.success, ...json.data }
  }
  return json
}

async function getUsersWithRegistrationStats() {
  const response = await flex.flexFetch(
    admin_tools_endpoint + 'getUsersWithRegistrationStats',
    {
      method: 'GET',
      credentials: 'include',
    },
  )
  const json = await flex.handleResponse(response)
  // Normalize strict envelope: server returns { success, data: [...users] }
  // Callers expect array directly
  return json.data !== undefined ? json.data : json
}

// Admin-only: Get all availability records across all players
async function getAllAvailability(options = {}) {
  const {
    limit = 50,
    offset = 0,
    leagueId,
    divisionId,
    startDate,
    endDate,
    isRecurring,
    search,
    sortBy,
    sortDirection,
  } = options

  const params = new URLSearchParams()
  params.append('limit', limit.toString())
  params.append('offset', offset.toString())
  if (leagueId) params.append('leagueId', leagueId.toString())
  if (divisionId) params.append('divisionId', divisionId.toString())
  if (startDate) params.append('startDate', startDate)
  if (endDate) params.append('endDate', endDate)
  if (isRecurring !== undefined)
    params.append('isRecurring', isRecurring.toString())
  if (search) params.append('search', search)
  if (sortBy) params.append('sortBy', sortBy)
  if (sortDirection) params.append('sortDirection', sortDirection)

  const response = await flex.flexFetch(
    admin_tools_endpoint + `getAllAvailability?${params.toString()}`,
    {
      method: 'GET',
      credentials: 'include',
    },
  )
  const json = await flex.handleResponse(response)
  // Normalize strict envelope: server returns { success, data: { availabilities, total, pagination } }
  // Callers expect: { availabilities, total, pagination }
  return json.data !== undefined ? json.data : json
}

// Admin-only: Get all match proposals across all players
async function getAllMatchProposals(options = {}) {
  const {
    limit = 50,
    offset = 0,
    leagueId,
    divisionId,
    status,
    startDate,
    endDate,
    search,
    sortBy,
    sortDirection,
  } = options

  const params = new URLSearchParams()
  params.append('limit', limit.toString())
  params.append('offset', offset.toString())
  if (leagueId) params.append('leagueId', leagueId.toString())
  if (divisionId) params.append('divisionId', divisionId.toString())
  if (status) params.append('status', status)
  if (startDate) params.append('startDate', startDate)
  if (endDate) params.append('endDate', endDate)
  if (search) params.append('search', search)
  if (sortBy) params.append('sortBy', sortBy)
  if (sortDirection) params.append('sortDirection', sortDirection)

  const response = await flex.flexFetch(
    admin_tools_endpoint + `getAllMatchProposals?${params.toString()}`,
    {
      method: 'GET',
      credentials: 'include',
    },
  )
  const json = await flex.handleResponse(response)
  // Normalize strict envelope: server returns { success, data: { proposals, total, pagination } }
  // Callers expect: { proposals, total, pagination }
  return json.data !== undefined ? json.data : json
}

const ApiAdminTools = {
  impersonateUser,
  stopImpersonate,
  getImpersonateStatus,
  getPageFlows,
  updateUser,
  getUsersWithRegistrationStats,
  getAllAvailability,
  getAllMatchProposals,
}

export default ApiAdminTools
